import { TSFix } from '@spotify-internal/ts-migration-utils/types';
import { createAuth } from '@spotify-internal/oauth-silent-authentication';

const BASE_URL = 'https://generic.wg.spotify.com';
export const PODCASTERS_SERVICE = `${BASE_URL}/podcasters`;

const CLIENT_ID = '05a1371ee5194c27860b3ff3ff3979d2';
const API_SCOPES = ['streaming', 'ugc-image-upload', 'user-read-email', 'user-read-private'];

export const auth = createAuth({
  clientId: CLIENT_ID,
  scopes: API_SCOPES,
});

export const login = () => auth.login(`${window.location.origin}/gateway`);

export const toJSON = (response: Response) => response.json();
export const asString = (response: Response) => response.text();

export class ResponseError extends Error {
  response: TSFix;

  constructor(response, name = 'ResponseError') {
    super(response.status);
    this.name = name;
    this.response = response;
  }
}

export class TimeoutError extends Error {
  constructor(status = 509, name = 'TimeoutError') {
    super(String(status));
    this.name = name;
  }
}

const delay = async (milliseconds: number) =>
  new Promise(resolve => setTimeout(resolve, milliseconds));

/* eslint-disable no-await-in-loop, no-continue */
export const poll = async (
  pollingRequest: () => any,
  options = { maxTries: 60, interval: 500 },
) => {
  let tries = 0;

  while (tries <= options.maxTries) {
    tries += 1;
    try {
      return await pollingRequest();
    } catch (err) {
      // server responds with 404 if the feed is not processed yet
      if (err.response.status === 404) {
        await delay(options.interval);
        continue;
      }
      throw err;
    }
  }
  throw new TimeoutError();
};

type RequestOptions = {
  spotifyAppVersion: string;
  anonymous?: boolean;
};

export class Request {
  options: RequestOptions;

  constructor(options: RequestOptions) {
    this.options = options;
  }

  static of(options: Pick<RequestOptions, 'anonymous'> = { anonymous: false }) {
    const pkgVersion = document.body.dataset.pkgversion;
    const version = document.body.dataset.version;

    return new Request({
      spotifyAppVersion: `${pkgVersion}.${version}`,
      anonymous: options.anonymous,
    });
  }

  getDefaultHeaders(url: string) {
    const headers = {
      Accept: 'application/json',
    };

    if (url.indexOf(PODCASTERS_SERVICE) === 0) {
      Object.assign(headers, {
        'Spotify-App-Version': this.options.spotifyAppVersion,
        'App-Platform': 'Browser',
      });
    }

    return headers;
  }

  fetch(url: string, options: RequestInit = {}) {
    return auth
      .fetch(url, {
        method: 'GET',
        ...options,
        headers: {
          ...this.getDefaultHeaders(url),
          ...options.headers,
        },
      })
      .catch(e => {
        // Ignore not authed errors
        if (e.error === 'login_required') {
          return e;
        } else {
          throw e;
        }
      });
  }

  async get(url: string) {
    const response = await this.fetch(url);
    if (!response.ok) throw new ResponseError(response);
    return response;
  }

  async post(url: string, body?: any) {
    const response = await this.fetch(url, {
      body: JSON.stringify(body),
      method: 'POST',
    });
    if (!response.ok) {
      throw new ResponseError(response);
    }
    return response;
  }

  async put(url: string, body?: any) {
    const response = await this.fetch(url, {
      body: JSON.stringify(body),
      method: 'PUT',
    });

    if (!response.ok) throw new ResponseError(response);
    return response;
  }

  async delete(url: string, body?: any) {
    const response = await this.fetch(url, {
      body: JSON.stringify(body),
      method: 'DELETE',
    });

    if (!response.ok) throw new ResponseError(response);
    return response;
  }
}
